/**
 * Admin-on-behalf capital edit for a managed user's broker account.
 *
 * Backend: PUT /admin/managed-users/{user}/broker-accounts/{client}/capital
 *  - Writes broker-accounts.capital
 *  - Best-effort propagates to today's analytics-daily row
 *  - Emits `capital_set_on_behalf` audit log with before/after values
 *
 * Validation: capital must be a positive number. No upper bound — that's an
 * operational decision the AM owns. Backend re-validates so this is UX only.
 */
import React, { useEffect, useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import managedBrokerService from '../../services/managedBrokerService';

interface EditCapitalDialogProps {
  open: boolean;
  managedUserId: string;
  managedClientId: string;
  managedDisplayName: string;
  currentCapital: number;
  onClose: () => void;
  onSuccess: (newCapital: number) => void;
}

const EditCapitalDialog: React.FC<EditCapitalDialogProps> = ({
  open,
  managedUserId,
  managedClientId,
  managedDisplayName,
  currentCapital,
  onClose,
  onSuccess,
}) => {
  const [capitalInput, setCapitalInput] = useState<string>(String(currentCapital || 0));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCapitalInput(String(currentCapital || 0));
      setError(null);
    }
  }, [open, currentCapital]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    const parsed = Number(capitalInput);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Capital must be a positive number');
      return;
    }
    if (parsed === currentCapital) {
      setError('New capital matches the current value — nothing to update');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await managedBrokerService.setCapitalOnBehalf(
        managedUserId, managedClientId, parsed,
      );
      onSuccess(result.capital);
    } catch (err: any) {
      setError(err.message || 'Failed to update capital');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Modal isOpen={open} onClose={handleClose} maxWidth="md" ariaLabel="Set Capital">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Set Capital</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              On behalf of <span className="font-medium">{managedDisplayName}</span> ({managedClientId})
            </p>
          </div>
          <button onClick={handleClose} disabled={isSubmitting}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Capital (₹) *
            </label>
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step="1"
              value={capitalInput}
              onChange={(e) => { setCapitalInput(e.target.value); setError(null); }}
              disabled={isSubmitting}
              placeholder="e.g. 250000"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Current: ₹{currentCapital.toLocaleString('en-IN')}. Capital edits propagate
              to today's analytics row if it already exists; otherwise the next cron run
              picks up the new value.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting} disabled={isSubmitting}>
            Update
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditCapitalDialog;
